import { MoneyDisplay } from "@/components/accounting/money-display";
import { PeriodSelector } from "@/components/accounting/period-selector";
import {
	ACCOUNT_TYPE_COLOR,
	ACCOUNT_TYPE_I18N,
} from "@/components/accounting/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { Search } from "lucide-react";
import { Side } from "@/gen/omnios/v1/accounting_pb";
import {
	useGetAccount,
	useGetAccountStatement,
	useListPeriods,
} from "@/hooks/use-accounting";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { formatDate } from "@/lib/format";
import { pushRecentItem } from "@/lib/recent-accounting";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";

import type { ColDef, ValueFormatterParams } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/accounting/accounts/$id")({
	component: AccountStatementPage,
});

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}

function AccountStatementPage() {
	const { t } = useTranslation("accounting");
	const { id } = Route.useParams();
	const gridRef = useRef<AgGridReact>(null);
	const { data: account, isLoading: accountLoading } = useGetAccount(id);

	useBreadcrumbs([
		{ label: t("accounts"), href: "/accounting/accounts" },
		{ label: account ? `${account.code} ${account.name}` : id },
	]);

	useEffect(() => {
		if (account) {
			pushRecentItem({
				type: "account",
				id: account.code,
				label: `${account.code} ${account.name}`,
				path: `/accounting/accounts/${id}`,
			});
		}
	}, [account?.code, id]);
	const { data: periodsData } = useListPeriods();
	const [periodId, setPeriodId] = useState("");

	const periods =
		periodsData?.periods.map((p) => ({
			id: p.periodId,
			label: p.periodId,
			status:
				p.status === 1 ? "open" : p.status === 3 ? "closed" : "soft-closed",
		})) ?? [];

	const accountCode = account?.code ?? "";

	const selectedPeriod = periodsData?.periods.find(
		(p) => p.periodId === periodId,
	);

	const { data: statement, isLoading: statementLoading } =
		useGetAccountStatement(
			accountCode,
			selectedPeriod?.startDate && selectedPeriod?.endDate
				? { fromDate: selectedPeriod.startDate, toDate: selectedPeriod.endDate }
				: undefined,
		);

	const isLoading = accountLoading || statementLoading;
	const isDebitNormal = account?.normalBalance === Side.DEBIT;

	const totalDebits =
		statement?.lines.reduce(
			(sum, l) => sum + Number.parseFloat(l.debit || "0"),
			0,
		) ?? 0;
	const totalCredits =
		statement?.lines.reduce(
			(sum, l) => sum + Number.parseFloat(l.credit || "0"),
			0,
		) ?? 0;

	type StatementLine = NonNullable<typeof statement>["lines"][number];

	const columnDefs = useMemo<ColDef<StatementLine>[]>(
		() => [
			{
				field: "date",
				headerName: t("entry_date"),
				width: 100,
				valueFormatter: (p: ValueFormatterParams<StatementLine>) => {
					const d = p.data?.date;
					return d ? formatDate(d.toDate(), "dd.MM.yyyy") : "—";
				},
			},
			{
				field: "entryNumber",
				headerName: t("entry_number"),
				width: 100,
				cellClass: "font-mono text-primary cursor-pointer hover:underline",
			},
			{
				field: "description",
				headerName: t("entry_description"),
				flex: 1,
				minWidth: 200,
			},
			{
				headerName: t("line_debit"),
				width: 130,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) => Number.parseFloat(p.data?.debit || "0") || null,
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "",
			},
			{
				headerName: t("line_credit"),
				width: 130,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) => Number.parseFloat(p.data?.credit || "0") || null,
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "",
			},
			{
				field: "runningBalance",
				headerName: t("running_balance"),
				width: 150,
				type: "rightAligned",
				cellClass: "font-tabular font-medium",
				valueGetter: (p) =>
					Number.parseFloat(p.data?.runningBalance || "0"),
				valueFormatter: (p: ValueFormatterParams) =>
					formatAmount(p.value ?? 0),
				cellStyle: (p) => {
					const val = p.value ?? 0;
					const isNormal = isDebitNormal ? val >= 0 : val <= 0;
					return isNormal ? null : { color: "hsl(var(--destructive))" };
				},
			},
		],
		[t, isDebitNormal],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const pinnedBottomRowData = useMemo(() => {
		if (!statement?.lines.length) return [];
		return [
			{
				date: null,
				entryNumber: t("total"),
				entryId: "",
				description: "",
				debit: String(totalDebits),
				credit: String(totalCredits),
				runningBalance: statement.closingBalance ?? "0",
			} as unknown as StatementLine,
		];
	}, [statement, totalDebits, totalCredits, t]);

	const onCellClicked = useCallback(
		(e: { colDef: ColDef; data: StatementLine | undefined }) => {
			if (
				e.colDef.field === "entryNumber" &&
				e.data?.entryId
			) {
				window.location.href = `/accounting/journal/${e.data.entryId}`;
			}
		},
		[],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "account",
				content: `${accountCode} ${account?.name ?? ""}`,
				align: "left" as const,
			},
			{
				id: "lines",
				content: t("row_count", { count: statement?.lines.length ?? 0 }),
				align: "left" as const,
			},
		],
		[accountCode, account?.name, statement?.lines.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	if (accountLoading) {
		return (
			<div className="space-y-4 p-3">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-6 w-48" />
				<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={i} className="h-24 rounded-lg" />
					))}
				</div>
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	if (!account) {
		return (
			<div className="flex items-center justify-center h-full">
				<p className="text-sm text-muted-foreground">{t("no_accounts")}</p>
			</div>
		);
	}

	const typeKey = ACCOUNT_TYPE_I18N[account.accountType];
	const typeColor = ACCOUNT_TYPE_COLOR[account.accountType] ?? "";

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: `${accountCode}_statement.csv`,
						})
					}
				>
					CSV
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => window.print()}
				>
					{t("toolbar_print")}
				</Button>
			</div>
			{/* Header */}
			<div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
				<span className="font-mono text-base font-bold">{account.code}</span>
				<span className="text-sm text-muted-foreground">{account.name}</span>
				{typeKey && (
					<Badge variant="secondary" className={`${typeColor} text-[10px]`}>
						{t(typeKey)}
					</Badge>
				)}
				<Badge variant="outline" className="text-[10px]">
					{account.normalBalance === Side.DEBIT
						? t("normal_debit")
						: t("normal_credit")}
				</Badge>
				<div className="ml-auto">
					<PeriodSelector
						value={periodId}
						onChange={setPeriodId}
						periods={periods}
					/>
				</div>
			</div>

			{/* Balance summary cards */}
			<div className="grid grid-cols-4 gap-px bg-border border-b shrink-0">
				{statementLoading ? (
					Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={i} className="h-[72px]" />
					))
				) : (
					<>
						<div className="bg-background px-3 py-2">
							<p className="text-[10px] text-muted-foreground">
								{t("opening_balance")}
							</p>
							<MoneyDisplay
								amount={statement?.openingBalance ?? "0"}
								currency={account.currency || "RUB"}
								className="text-lg font-semibold"
							/>
						</div>
						<div className="bg-background px-3 py-2">
							<p className="text-[10px] text-muted-foreground">
								{t("total_debits")}
							</p>
							<MoneyDisplay
								amount={String(totalDebits)}
								currency={account.currency || "RUB"}
								className="text-lg font-semibold"
							/>
						</div>
						<div className="bg-background px-3 py-2">
							<p className="text-[10px] text-muted-foreground">
								{t("total_credits")}
							</p>
							<MoneyDisplay
								amount={String(totalCredits)}
								currency={account.currency || "RUB"}
								className="text-lg font-semibold"
							/>
						</div>
						<div className="bg-background px-3 py-2">
							<p className="text-[10px] text-muted-foreground">
								{t("closing_balance")}
							</p>
							<MoneyDisplay
								amount={statement?.closingBalance ?? "0"}
								currency={account.currency || "RUB"}
								className="text-lg font-semibold"
								colorize
							/>
						</div>
					</>
				)}
			</div>

			{/* AG Grid */}
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
				<AgGridReact<StatementLine>
					ref={gridRef}
					rowData={statement?.lines ?? []}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowHeight={28}
					headerHeight={32}
					animateRows={false}
					getRowId={(p) =>
						`${p.data.entryId}-${p.data.entryNumber}-${p.data.debit}-${p.data.credit}`
					}
					loading={isLoading}
					pinnedBottomRowData={pinnedBottomRowData}
					onCellClicked={onCellClicked}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("no_statement_lines")}</p></div>`}
				/>
			</div>
		</div>
	);
}
